import { z } from 'zod';

/**
 * A member's focus hours (PLAN-GENERICITE § 4 sexies, step 7): where a focus block may be placed in
 * their calendar (« Caler un créneau », Otto's work blocks). The shape is the company setting's
 * (`flux.focus_hours`): ISO days 1 (Monday) to 7, windows « HH:MM » in the company's time zone. Null:
 * the company's hours apply. The database holds the same rule (`focus_hours_valid`), this module only
 * spares a round trip and says what is wrong in the screen's words.
 */

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

export const focusHoursSchema = z
  .object({
    days: z.array(z.number().int().min(1).max(7)).min(1).max(7).readonly(),
    windows: z.array(z.tuple([z.string().regex(HHMM), z.string().regex(HHMM)]).readonly()).min(1).max(6).readonly(),
  })
  .readonly();

export type FocusHours = z.infer<typeof focusHoursSchema>;

export const MAX_WINDOWS = 6;

/** The week, Monday first, with the ISO number the database stores. */
export const WEEK_DAYS: ReadonlyArray<{ readonly iso: number; readonly short: string; readonly label: string }> = [
  { iso: 1, short: 'Lun', label: 'lundi' },
  { iso: 2, short: 'Mar', label: 'mardi' },
  { iso: 3, short: 'Mer', label: 'mercredi' },
  { iso: 4, short: 'Jeu', label: 'jeudi' },
  { iso: 5, short: 'Ven', label: 'vendredi' },
  { iso: 6, short: 'Sam', label: 'samedi' },
  { iso: 7, short: 'Dim', label: 'dimanche' },
];

/** What the editor holds: the company's hours, or the member's own days and windows as typed. */
export interface FocusDraft {
  readonly own: boolean;
  readonly days: readonly number[];
  readonly windowsText: string;
}

/**
 * Where « Ses propres heures » starts: the hours applied when the company has set none
 * (`DEFAULT_FOCUS_HOURS`, supabase/functions/_shared/focusSlots.ts), so that saving without an edit
 * changes nothing (audit of 09/10/2026).
 */
const DEFAULT_DRAFT: FocusDraft = { own: false, days: [1, 2, 3, 4, 5], windowsText: '09:00-12:30, 13:30-18:30' };

/** « 09:00-12:00, 14:00-18:00 ». */
export function formatWindows(windows: FocusHours['windows']): string {
  return windows.map(([from, to]) => `${from}-${to}`).join(', ');
}

export function focusDraftOf(hours: FocusHours | null): FocusDraft {
  if (!hours) return DEFAULT_DRAFT;
  return { own: true, days: [...hours.days].sort((a, b) => a - b), windowsText: formatWindows(hours.windows) };
}

/** « 9:00 » or « 09h00 » → « 09:00 »; anything else unchanged (then refused). */
function normalizeTime(raw: string): string {
  const match = /^(\d{1,2})[:h](\d{2})$/.exec(raw.trim());
  return match ? `${match[1].padStart(2, '0')}:${match[2]}` : raw.trim();
}

export type FocusResult =
  | { readonly ok: true; readonly hours: FocusHours | null }
  | { readonly ok: false; readonly error: string };

/** The hours to store (null = the company's), or what the screen says is wrong. Pure. */
export function focusHoursFromDraft(draft: FocusDraft): FocusResult {
  if (!draft.own) return { ok: true, hours: null };
  const days = [...new Set(draft.days)].sort((a, b) => a - b);
  if (days.length === 0) return { ok: false, error: 'Choisissez au moins un jour.' };
  const parts = draft.windowsText.split(/[,;]/).map((part) => part.trim()).filter(Boolean);
  if (parts.length === 0) return { ok: false, error: 'Indiquez au moins une plage, par exemple 09:00-12:00.' };
  if (parts.length > MAX_WINDOWS) return { ok: false, error: `${MAX_WINDOWS} plages au plus.` };
  const windows: Array<readonly [string, string]> = [];
  for (const part of parts) {
    const bounds = part.split(/\s*(?:-|–|—|à)\s*/);
    if (bounds.length !== 2) return { ok: false, error: `« ${part} » : une plage s'écrit début-fin, par exemple 09:00-12:00.` };
    const [from, to] = bounds.map(normalizeTime);
    if (!HHMM.test(from) || !HHMM.test(to)) return { ok: false, error: `« ${part} » : les heures s'écrivent HH:MM.` };
    if (from >= to) return { ok: false, error: `« ${part} » : le début vient avant la fin.` };
    windows.push([from, to]);
  }
  windows.sort(([a], [b]) => a.localeCompare(b));
  return { ok: true, hours: { days, windows } };
}

/** Same hours, whatever the order the days and windows were typed in. */
export function sameFocusHours(a: FocusHours | null, b: FocusHours | null): boolean {
  if (!a || !b) return a === b;
  const key = (h: FocusHours) => JSON.stringify([[...h.days].sort((x, y) => x - y), [...h.windows].map((w) => w.join('-')).sort()]);
  return key(a) === key(b);
}

/** « lundi au vendredi, 09:00-12:00, 14:00-18:00 », for the card of a member. */
export function describeFocusHours(hours: FocusHours | null): string {
  if (!hours) return "Heures de l'entreprise";
  const days = [...hours.days].sort((a, b) => a - b);
  const contiguous = days.length > 2 && days.every((day, i) => i === 0 || day === days[i - 1] + 1);
  const label = (iso: number) => WEEK_DAYS[iso - 1].label;
  const dayText = contiguous ? `du ${label(days[0])} au ${label(days[days.length - 1])}` : days.map(label).join(', ');
  return `${dayText}, ${formatWindows(hours.windows)}`;
}
